/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { isDate } from "lodash";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { useAppDispatch } from "../../../store/rootStore";
import { selectCurrentCart } from "../../cart/storage/cart.slice";
import AuthSlice, { BasicDigitalFarmerAccount, selectAllFarmerAccounts } from "../storage";

interface AccountSwitcherContextType {
  accounts: BasicDigitalFarmerAccount[];
  filteredAccounts: BasicDigitalFarmerAccount[];
  recentViews: BasicDigitalFarmerAccount[];
  searchText: string;
  searchEnabled: boolean;
  openAlertModal: boolean;
  switchId: string | undefined;
  setSearchText: (text: string) => void;
  handleOnSearch: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChange: (event: React.SyntheticEvent<Element, Event> | undefined, value: string) => boolean;
  setOpenAlertModal: (open: boolean) => void;
}

const AccountSwitcherContext = createContext<AccountSwitcherContextType | undefined>(undefined);

export const AccountSwitcherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const farmlandsAccounts = useSelector(selectAllFarmerAccounts);
  const cart = useSelector(selectCurrentCart);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const accountNumber = query.get("accountNumber");

  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [switchId, setSwitchId] = useState<string | undefined>(undefined);
  const [filteredAccounts, setFilteredAccounts] = useState<BasicDigitalFarmerAccount[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [searchEnabled, setSearchEnabled] = useState<boolean>(false);

  const accounts = useMemo(() => {
    const accounts: BasicDigitalFarmerAccount[] = [];
    if (farmlandsAccounts) {
      const keys = Object.keys(farmlandsAccounts.accounts);
      keys.forEach((key) => accounts.push(farmlandsAccounts.accounts[key]));
    }
    return accounts.sort((a, b) => a.accountName.localeCompare(b.accountName));
  }, [farmlandsAccounts]);

  useEffect(() => {
    setFilteredAccounts([...accounts].sort((a, b) => a.accountName.localeCompare(b.accountName)));
    setSearchEnabled(accounts.length >= 10);
  }, [accounts]);

  const removeAccountNumberFromQueryParams = () => {
    // Remove the 'accountNumber' parameter
    query.delete("accountNumber");
  };

  useEffect(() => {
    if (accountNumber && accounts.length) {
      const matchedAccount = accounts.find((account) => account.accountNumber === accountNumber);
      if (matchedAccount) {
        dispatch(AuthSlice.actions.setSelectedFarmerAccount(accountNumber));
      }
      // Leave exposed? Or add another use effect to sync ?accountNumber=  with account switcher?
      removeAccountNumberFromQueryParams();
    }
  }, [accountNumber, accounts.length]);

  const recentViews = useMemo(
    () =>
      accounts
        .filter((account) => isDate(account.lastViewedAt))
        .sort((a, b) => new Date(b.lastViewedAt!).getTime() - new Date(a.lastViewedAt!).getTime())
        .slice(0, 3),
    [accounts]
  );

  const handleOnSearch = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = event.target.value;
    setSearchText(inputValue);
    const filtered = accounts.filter(
      (account) =>
        account.accountNumber.includes(inputValue.toString()) ||
        account.accountName.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredAccounts(filtered);
  };

  const onChange = (event: React.SyntheticEvent<Element, Event> | undefined, value: string) => {
    if (cart && cart.items.length > 0) {
      event?.preventDefault();
      event?.stopPropagation();
      setSwitchId(accounts.find((e) => e.accountNumber === value)?.accountNumber);
      setOpenAlertModal(true);
      return false;
    } else {
      const id = accounts.find((e) => e.accountNumber === value)?.accountNumber;
      if (id) {
        dispatch(AuthSlice.actions.setSelectedFarmerAccount(id));
      }

      return true;
    }
  };

  const contextValue = useMemo(
    () => ({
      accounts,
      filteredAccounts,
      recentViews,
      searchText,
      searchEnabled,
      openAlertModal,
      switchId,
      setSearchText,
      handleOnSearch,
      onChange,
      setOpenAlertModal,
    }),
    [
      accounts,
      filteredAccounts,
      recentViews,
      searchText,
      searchEnabled,
      openAlertModal,
      switchId,
      handleOnSearch,
      onChange,
    ]
  );

  return (
    <AccountSwitcherContext.Provider value={contextValue}>
      {children}
    </AccountSwitcherContext.Provider>
  );
};

export const useAccountSwitcher = (): AccountSwitcherContextType => {
  const context = useContext(AccountSwitcherContext);
  if (!context) {
    throw new Error("useAccountSwitcher must be used within an AccountSwitcherProvider");
  }
  return context;
};
